import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Store } from "react-notifications-component";
import { User, Lock } from "lucide-react";

import AdminPageShell from "./AdminPageShell";
import setAuthToken from "../../utils/setAuthToken";
import { setCurrentUser } from "../../actions/authActions";

import {
  PageHeader,
  Card,
  Input,
  FormField,
  Alert,
  Button,
  Spinner,
  UserAvatar,
} from "../../design-system";

class UserProfile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      name: "",
      email: "",
      loading: false,
      saving: false,
      profileErrors: {},
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      passwordErrors: {},
      changingPassword: false,
    };
  }

  componentDidMount() {
    this.loadProfile();
  }

  loadProfile = async () => {
    try {
      this.setState({ loading: true });
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const response = await axios.get("/api/auth/me");
      if (response.data.success) {
        const user = response.data.user;
        this.setState({ name: user.name || "", email: user.email || "", loading: false });
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur", message: "Impossible de charger le profil",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
    }
  };

  handleProfileChange = (e) => {
    this.setState({ [e.target.name]: e.target.value, profileErrors: {} });
  };

  handlePasswordChange = (e) => {
    this.setState({ [e.target.name]: e.target.value, passwordErrors: {} });
  };

  validateProfile = () => {
    const errors = {};
    if (!this.state.name || this.state.name.trim().length < 2)
      errors.name = "Le nom doit contenir au moins 2 caractères";
    if (!this.state.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.state.email))
      errors.email = "L'email n'est pas valide";
    return errors;
  };

  validatePassword = () => {
    const errors = {};
    if (!this.state.currentPassword) errors.currentPassword = "Le mot de passe actuel est requis";
    if (!this.state.newPassword || this.state.newPassword.length < 6)
      errors.newPassword = "Le nouveau mot de passe doit contenir au moins 6 caractères";
    if (this.state.newPassword !== this.state.confirmPassword)
      errors.confirmPassword = "Les mots de passe ne correspondent pas";
    return errors;
  };

  handleSaveProfile = async (e) => {
    e.preventDefault();
    const errors = this.validateProfile();
    if (Object.keys(errors).length > 0) {
      this.setState({ profileErrors: errors });
      return;
    }
    try {
      this.setState({ saving: true });
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const response = await axios.put("/api/auth/profile", {
        name: this.state.name, email: this.state.email,
      });
      if (response.data.success) {
        this.props.setCurrentUser(response.data.user);
        Store.addNotification({
          title: "Profil mis à jour",
          message: "Tes informations ont été enregistrées.",
          type: "success", insert: "top", container: "top-right", dismiss: { duration: 3000 },
        });
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      const errorData = error.response?.data || {};
      this.setState({
        profileErrors: errorData.errors || { general: errorData.message || "Erreur lors de la mise à jour" },
      });
      Store.addNotification({
        title: "Erreur",
        message: errorData.message || "Erreur lors de la mise à jour du profil",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 5000 },
      });
    } finally {
      this.setState({ saving: false });
    }
  };

  handleChangePassword = async (e) => {
    e.preventDefault();
    const errors = this.validatePassword();
    if (Object.keys(errors).length > 0) {
      this.setState({ passwordErrors: errors });
      return;
    }
    try {
      this.setState({ changingPassword: true });
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const response = await axios.put("/api/auth/password", {
        currentPassword: this.state.currentPassword,
        newPassword: this.state.newPassword,
        confirmPassword: this.state.confirmPassword,
      });
      if (response.data.success) {
        Store.addNotification({
          title: "Mot de passe modifié",
          type: "success", insert: "top", container: "top-right", dismiss: { duration: 3000 },
        });
        this.setState({
          currentPassword: "", newPassword: "", confirmPassword: "", passwordErrors: {},
        });
      }
    } catch (error) {
      console.error("Error changing password:", error);
      const errorData = error.response?.data || {};
      this.setState({
        passwordErrors: errorData.errors || { general: errorData.message || "Erreur lors du changement" },
      });
      Store.addNotification({
        title: "Erreur",
        message: errorData.message || "Erreur lors du changement de mot de passe",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 5000 },
      });
    } finally {
      this.setState({ changingPassword: false });
    }
  };

  render() {
    const { user } = this.props.auth;
    const { loading, saving, changingPassword, profileErrors, passwordErrors } = this.state;

    if (loading) {
      return (
        <AdminPageShell pageTitle="Mon profil">
          <div className="flex items-center justify-center py-20 text-mute"><Spinner size="lg" /></div>
        </AdminPageShell>
      );
    }

    return (
      <AdminPageShell pageTitle="Mon profil">
        <PageHeader
          eyebrow="— Compte"
          title="Mon profil"
          description="Gère ton identité et ton mot de passe."
        />

        <div className="max-w-3xl space-y-5">
          {/* Carte identité avec avatar */}
          <Card variant="filled">
            <div className="flex items-center gap-4">
              <UserAvatar user={user} size="xl" />
              <div>
                <div className="text-lg font-bold tracking-tight">{user?.name || user?.email}</div>
                <div className="text-sm text-mute">{user?.email}</div>
                <div className="text-xs text-mute-2 mt-1 font-mono">
                  Rôle : {user?.role === "ADMIN" ? "Administrateur" : "Utilisateur"}
                </div>
              </div>
            </div>
          </Card>

          {/* Profil */}
          <Card>
            <Card.Header>
              <Card.Title className="flex items-center gap-2">
                <User className="w-4 h-4" strokeWidth={1.8} />Informations du profil
              </Card.Title>
            </Card.Header>
            <form onSubmit={this.handleSaveProfile} className="space-y-4">
              {profileErrors.general && (
                <Alert variant="danger">{profileErrors.general}</Alert>
              )}
              <FormField label="Nom" required error={profileErrors.name}>
                <Input
                  name="name"
                  value={this.state.name}
                  onChange={this.handleProfileChange}
                  required
                />
              </FormField>
              <FormField label="Email" required error={profileErrors.email}>
                <Input
                  type="email"
                  name="email"
                  value={this.state.email}
                  onChange={this.handleProfileChange}
                  required
                />
              </FormField>
              <div className="flex justify-end">
                <Button type="submit" isLoading={saving}>Enregistrer</Button>
              </div>
            </form>
          </Card>

          {/* Mot de passe */}
          <Card>
            <Card.Header>
              <Card.Title className="flex items-center gap-2">
                <Lock className="w-4 h-4" strokeWidth={1.8} />Changer le mot de passe
              </Card.Title>
            </Card.Header>
            <form onSubmit={this.handleChangePassword} className="space-y-4">
              {passwordErrors.general && (
                <Alert variant="danger">{passwordErrors.general}</Alert>
              )}
              <FormField label="Mot de passe actuel" required error={passwordErrors.currentPassword}>
                <Input
                  type="password"
                  name="currentPassword"
                  value={this.state.currentPassword}
                  onChange={this.handlePasswordChange}
                  required
                />
              </FormField>
              <FormField label="Nouveau mot de passe" required error={passwordErrors.newPassword}>
                <Input
                  type="password"
                  name="newPassword"
                  value={this.state.newPassword}
                  onChange={this.handlePasswordChange}
                  required
                  minLength={6}
                />
              </FormField>
              <FormField label="Confirmation" required error={passwordErrors.confirmPassword}>
                <Input
                  type="password"
                  name="confirmPassword"
                  value={this.state.confirmPassword}
                  onChange={this.handlePasswordChange}
                  required
                />
              </FormField>
              <div className="flex justify-end">
                <Button type="submit" isLoading={changingPassword}>
                  Changer le mot de passe
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </AdminPageShell>
    );
  }
}

const mapStateToProps = (state) => ({ auth: state.auth });

export default connect(mapStateToProps, { setCurrentUser })(UserProfile);
